import axios from "axios";
import { getTokenHeader } from "@/helpers/commonHelper";
import ColumnVisibilitySelector from "@/components/Common/ColumnVisibilitySelector/ColumnVisibilitySelector.vue";

const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);
const BASE = `${env.prompts_api_client_url}api/eo-prompt/interference`;

export default {
  name: "LlmStats",
  components: {
    ColumnVisibilitySelector
  },
  data() {
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setDate(today.getDate() - 30);
    const defaultHeaders = [
      { text: "Datum", value: "createdAt" },
      { text: "Aktualisiert", value: "updatedAt" },
      { text: "EO API Key", value: "eo_apikey" },
      { text: "Prompt", value: "apiShortId" },
      { text: "Prompt Short ID", value: "promptShortId" },
      { text: "User", value: "user" },
      { text: "Model", value: "model" },
      { text: "Feedback", value: "responseFeedback" },
      { text: "Stop Reason", value: "stopReason" },
      { text: "Input Tokens", value: "inputTokens" },
      { text: "Output Tokens", value: "outputTokens" },
      { text: "Question", value: "question" },
      { text: "Payload", value: "payload" },
      { text: "LLM Result", value: "llmResult" }
    ];

    return {
      loading: false,
      exportLoading: false,
      expanded: [],
      logs: [],
      stats: {
        total: 0,
        success: 0,
        errors: 0,
        errorRate: 0,
        totalInputTokens: 0,
        totalOutputTokens: 0,
        responseFeedback: {
          positive: 0,
          negative: 0,
          neutral: 0
        }
      },
      filterOptions: {
        eoApiKeys: [],
        apiShortIds: [],
        promptShortIds: [],
        stopReasons: [],
        models: []
      },
      eoApiKeyLabelMap: {},
      eoApiKeyPromptShortIdMap: {},
      promptLabelByApiShortId: {},
      promptLabelByShortId: {},
      filters: {
        eo_apikey: null,
        apiShortId: null,
        promptShortId: null,
        stopReason: null,
        model: null,
        hasError: null,
        hasFeedback: null,
        responseFeedback: null,
        search: "",
        fromDate: lastMonth.toISOString().split("T")[0],
        toDate: today.toISOString().split("T")[0]
      },
      hasErrorOptions: [
        { text: "Alle", value: null },
        { text: "Nur erfolgreich", value: false },
        { text: "Nur Fehler", value: true }
      ],
      hasFeedbackOptions: [
        { text: "Alle", value: null },
        { text: "Nur bewertet", value: true },
        { text: "Nur unbewertet", value: false }
      ],
      responseFeedbackOptions: [
        { text: "Neutral", value: 0 },
        { text: "Positiv", value: 1 },
        { text: "Negativ", value: -1 }
      ],
      pagination: {
        page: 1,
        limit: 25,
        total: 0,
        pages: 0
      },
      sortBy: "createdAt",
      sortOrder: "desc",
      menuFromDate: false,
      menuToDate: false,
      allHeaders: defaultHeaders,
      visibleColumnKeys: defaultHeaders.map(header => header.value)
    };
  },
  async created() {
    await this.loadAll();
  },
  computed: {
    currentPrincipal() {
      return this.$store.state.selectedPrincipal.id;
    },
    columnSettingsStorageKey() {
      return "llm_stats_interference_columns";
    },
    tableOptions() {
      return {
        page: this.pagination.page,
        itemsPerPage: this.pagination.limit,
        sortBy: [this.sortBy],
        sortDesc: [this.sortOrder === "desc"]
      };
    },
    columnOptions() {
      return this.allHeaders.map(header => ({
        text: header.text,
        value: header.value
      }));
    },
    visibleHeaders() {
      const selected = new Set(this.visibleColumnKeys);
      return this.allHeaders.filter(header => selected.has(header.value));
    },
    pageStart() {
      if (!this.pagination.total) return 0;
      return (this.pagination.page - 1) * this.pagination.limit + 1;
    },
    pageStop() {
      if (!this.pagination.total) return 0;
      return Math.min(
        this.pagination.page * this.pagination.limit,
        this.pagination.total
      );
    }
  },
  watch: {
    async currentPrincipal() {
      await this.loadAll();
    },
    visibleColumnKeys(nextValue) {
      if (Array.isArray(nextValue) && nextValue.length > 0) return;
      // Verhindert eine komplett leere Tabelle durch eine leere Auswahl.
      this.visibleColumnKeys = [this.allHeaders[0].value];
    }
  },
  methods: {
    normalizePromptKey(value) {
      return String(value || "")
        .trim()
        .toLowerCase();
    },
    normalizeFromDate(value) {
      if (!value) return "";
      return `${value}T00:00:00.000Z`;
    },
    normalizeToDate(value) {
      if (!value) return "";
      return `${value}T23:59:59.999Z`;
    },
    activeFilterParams() {
      const params = {};
      if (this.filters.eo_apikey) params.eo_apikey = this.filters.eo_apikey;
      if (this.filters.apiShortId) params.apiShortId = this.filters.apiShortId;
      if (this.filters.promptShortId)
        params.promptShortId = this.filters.promptShortId;
      if (this.filters.stopReason) params.stopReason = this.filters.stopReason;
      if (this.filters.model) params.model = this.filters.model;
      if (this.filters.search?.trim())
        params.search = this.filters.search.trim();
      if (this.filters.hasError !== null)
        params.hasError = this.filters.hasError;
      if (this.filters.hasFeedback !== null)
        params.hasFeedback = this.filters.hasFeedback;
      if (this.filters.responseFeedback !== null)
        params.responseFeedback = this.filters.responseFeedback;
      if (this.filters.fromDate) {
        params.fromDate = this.normalizeFromDate(this.filters.fromDate);
      }
      if (this.filters.toDate) {
        params.toDate = this.normalizeToDate(this.filters.toDate);
      }
      return params;
    },
    async onTableOptionsUpdated(nextOptions = {}) {
      const parsedPage = Number(nextOptions.page);
      const nextPage =
        Number.isFinite(parsedPage) && parsedPage > 0
          ? parsedPage
          : this.pagination.page;
      const parsedItemsPerPage = Number(nextOptions.itemsPerPage);
      const nextLimit =
        Number.isFinite(parsedItemsPerPage) && parsedItemsPerPage > 0
          ? parsedItemsPerPage
          : this.pagination.limit;

      let nextSortBy = this.sortBy;
      let nextSortOrder = this.sortOrder;
      if (Array.isArray(nextOptions.sortBy) && nextOptions.sortBy.length > 0) {
        nextSortBy = nextOptions.sortBy[0];
        if (
          Array.isArray(nextOptions.sortDesc) &&
          nextOptions.sortDesc.length > 0
        ) {
          nextSortOrder = nextOptions.sortDesc[0] ? "desc" : "asc";
        }
      }

      const hasChanged =
        nextPage !== this.pagination.page ||
        nextLimit !== this.pagination.limit ||
        nextSortBy !== this.sortBy ||
        nextSortOrder !== this.sortOrder;

      if (!hasChanged) return;

      this.pagination.page = nextPage;
      this.pagination.limit = nextLimit;
      this.sortBy = nextSortBy;
      this.sortOrder = nextSortOrder;
      await this.fetchLogs();
    },
    async loadAll() {
      this.pagination.page = 1;
      await this.fetchDisplaySources();
      await Promise.all([
        this.fetchFilters(),
        this.fetchStats(),
        this.fetchLogs()
      ]);
    },
    async fetchDisplaySources() {
      try {
        const headers = getTokenHeader();
        const [
          adminRes,
          principalPromptRes,
          sharedPromptRes
        ] = await Promise.all([
          axios.get(`${env.prompts_api_client_url}api/eo-prompt/admin`, {
            params: { principal_id: this.currentPrincipal },
            headers
          }),
          axios.get(`${env.prompts_api_client_url}api/eo-prompt/prompts`, {
            params: { principal_id: this.currentPrincipal },
            headers
          }),
          axios.get(`${env.prompts_api_client_url}api/eo-prompt/prompts`, {
            params: {
              principal_id: this.currentPrincipal,
              shared: true
            },
            headers
          })
        ]);

        const keyMap = {};
        const eoApiKeyPromptShortIdMap = {};
        (adminRes.data || []).forEach(entry => {
          if (!entry?.apiKey) return;
          keyMap[entry.apiKey] = this.buildEoApiKeyLabel(
            entry.description,
            entry.apiKey
          );

          const promptShortId = String(entry?.promptShortId || "").trim();
          if (promptShortId) {
            eoApiKeyPromptShortIdMap[
              this.normalizePromptKey(entry.apiKey)
            ] = promptShortId;
          }
        });
        this.eoApiKeyLabelMap = keyMap;
        this.eoApiKeyPromptShortIdMap = eoApiKeyPromptShortIdMap;

        const promptLabelByShortId = {};
        const promptLabelByApiShortId = {};
        const allPrompts = [
          ...(sharedPromptRes.data || []),
          ...(principalPromptRes.data || [])
        ];
        allPrompts.forEach(prompt => {
          const shortId = String(prompt?.shortId || "").trim();
          const apiShortId = String(prompt?.apiShortId || "").trim();
          const label = this.buildPromptLabel(
            prompt.name,
            shortId || apiShortId
          );

          if (shortId) {
            promptLabelByShortId[this.normalizePromptKey(shortId)] = label;
          }

          if (apiShortId) {
            promptLabelByApiShortId[
              this.normalizePromptKey(apiShortId)
            ] = label;
          }
        });

        this.promptLabelByShortId = promptLabelByShortId;
        this.promptLabelByApiShortId = promptLabelByApiShortId;
      } catch (err) {
        console.error("Failed to load display source data:", err);
      }
    },
    async fetchFilters() {
      try {
        const headers = getTokenHeader();
        const params = { principal_id: this.currentPrincipal };
        const res = await axios.get(`${BASE}/filters`, { params, headers });

        const eoKeys = res.data?.eoApiKeys || [];
        this.filterOptions.eoApiKeys = eoKeys.map(value => ({
          text: this.eoApiKeyLabelMap[value] || value,
          value
        }));

        const apiShortIds = res.data?.apiShortIds || [];
        this.filterOptions.apiShortIds = apiShortIds.map(value => ({
          text: this.formatPromptShortId(value),
          value
        }));

        const promptShortIds = res.data?.promptShortIds || [];
        this.filterOptions.promptShortIds = promptShortIds.map(value => ({
          text: this.formatPromptName(value),
          value
        }));

        const stopReasons = res.data?.stopReasons || [];
        this.filterOptions.stopReasons = stopReasons.map(value => ({
          text: value,
          value
        }));

        const models = res.data?.models || [];
        this.filterOptions.models = models.map(value => ({
          text: value,
          value
        }));

      } catch (err) {
        console.error("Failed to load interference filter options:", err);
      }
    },
    async fetchStats() {
      try {
        const headers = getTokenHeader();
        const params = {
          principal_id: this.currentPrincipal,
          ...this.activeFilterParams()
        };
        const res = await axios.get(`${BASE}/stats`, { params, headers });

        this.stats.total = res.data?.total || 0;
        this.stats.success = res.data?.success || 0;
        this.stats.errors = res.data?.errors || 0;
        this.stats.errorRate = res.data?.errorRate || 0;
        this.stats.totalInputTokens = res.data?.totalInputTokens || 0;
        this.stats.totalOutputTokens = res.data?.totalOutputTokens || 0;
        this.stats.responseFeedback = {
          positive: Number(res.data?.responseFeedback?.positive || 0),
          negative: Number(res.data?.responseFeedback?.negative || 0),
          neutral: Number(res.data?.responseFeedback?.neutral || 0)
        };
      } catch (err) {
        console.error("Failed to load interference stats:", err);
      }
    },
    async fetchLogs() {
      this.loading = true;
      try {
        const headers = getTokenHeader();
        const params = {
          principal_id: this.currentPrincipal,
          page: this.pagination.page,
          limit: this.pagination.limit,
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
          ...this.activeFilterParams()
        };
        const res = await axios.get(`${BASE}/logs`, { params, headers });
        this.logs = res.data?.items || [];
        this.pagination.total = res.data?.pagination?.total || 0;
        this.pagination.pages = res.data?.pagination?.pages || 0;
      } catch (err) {
        console.error("Failed to load interference logs:", err);
      } finally {
        this.loading = false;
      }
    },
    async applyFilters() {
      this.pagination.page = 1;
      await Promise.all([this.fetchStats(), this.fetchLogs()]);
    },
    async resetFilters() {
      const today = new Date();
      const lastMonth = new Date();
      lastMonth.setDate(today.getDate() - 30);

      this.filters = {
        eo_apikey: null,
        apiShortId: null,
        promptShortId: null,
        stopReason: null,
        model: null,
        hasError: null,
        hasFeedback: null,
        responseFeedback: null,
        search: "",
        fromDate: lastMonth.toISOString().split("T")[0],
        toDate: today.toISOString().split("T")[0]
      };
      await this.applyFilters();
    },
    async previousPage() {
      if (this.pagination.page <= 1) return;
      this.pagination.page -= 1;
      await this.fetchLogs();
    },
    async nextPage() {
      if (this.pagination.page >= (this.pagination.pages || 1)) return;
      this.pagination.page += 1;
      await this.fetchLogs();
    },
    async downloadCsv() {
      if (this.exportLoading) return;
      this.exportLoading = true;
      try {
        const allItems = await this.fetchAllFilteredLogs();
        const csv = this.buildCsv(allItems);
        const filename = this.buildCsvFilename();
        this.downloadCsvFile(csv, filename);
      } catch (err) {
        console.error("Failed to export interference logs as CSV:", err);
      } finally {
        this.exportLoading = false;
      }
    },
    downloadCsvFile(csvContent, filename) {
      // BOM sorgt dafür, dass Excel UTF-8 korrekt erkennt (Umlaute/Sonderzeichen).
      const blob = new Blob(["\uFEFF", csvContent], {
        type: "text/csv;charset=utf-8;"
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    async fetchAllFilteredLogs() {
      const headers = getTokenHeader();
      const pageSize = 500;
      let page = 1;
      let totalPages = 1;
      const allItems = [];

      while (page <= totalPages) {
        const params = {
          principal_id: this.currentPrincipal,
          page,
          limit: pageSize,
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
          ...this.activeFilterParams()
        };
        const res = await axios.get(`${BASE}/logs`, { params, headers });
        const items = res.data?.items || [];
        const pagination = res.data?.pagination || {};

        allItems.push(...items);
        totalPages = Number(pagination.pages || 1);
        page += 1;
      }

      return allItems;
    },
    buildCsv(items) {
      const csvHeaders = [
        "Datum",
        "Aktualisiert",
        "EO API Key",
        "Prompt",
        "Prompt Short ID",
        "User",
        "Model",
        "Feedback",
        "Stop Reason",
        "Input Tokens",
        "Output Tokens",
        "Question",
        "Payload",
        "LLM Result"
      ];

      const rows = items.map(item => [
        this.formatDate(item?.createdAt),
        this.formatDate(item?.updatedAt),
        this.formatEoApiKey(item?.eo_apikey),
        this.formatPromptShortId(item?.apiShortId, item?.eo_apikey),
        this.formatPromptName(item?.promptShortId),
        this.formatUser(item?.user),
        this.formatModel(item?.model),
        this.formatResponseFeedbackLabel(item?.responseFeedback),
        this.formatStopReason(item?.stopReason),
        item?.inputTokens ?? 0,
        item?.outputTokens ?? 0,
        item?.question || "",
        item?.payload || "",
        this.formatLlmResultPreview(item?.llmResult)
      ]);

      return [csvHeaders, ...rows]
        .map(row => row.map(this.escapeCsvValue).join(";"))
        .join("\n");
    },
    escapeCsvValue(value) {
      const normalized = String(value ?? "")
        .replace(/\r?\n|\r/g, " ")
        .trim();
      const escaped = normalized.replace(/"/g, '""');
      return `"${escaped}"`;
    },
    buildCsvFilename() {
      const now = new Date();
      const pad = value => String(value).padStart(2, "0");
      const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
        now.getDate()
      )}_${pad(now.getHours())}-${pad(now.getMinutes())}`;
      return `llm-interference-${timestamp}.csv`;
    },
    formatDate(date) {
      if (!date) return "-";
      const moment = require("moment");
      return moment(date).format("DD.MM.YYYY HH:mm:ss");
    },
    formatErrorRate(rate) {
      const safeRate = Number(rate || 0) * 100;
      return `${safeRate.toFixed(2)} %`;
    },
    formatEoApiKey(value) {
      if (!value) return "-";
      return this.eoApiKeyLabelMap[value] || value;
    },
    getPromptLabelViaAdminMapping(eoApiKey) {
      const normalizedApiKey = this.normalizePromptKey(eoApiKey);
      if (!normalizedApiKey) return "";

      const promptShortId = this.eoApiKeyPromptShortIdMap[normalizedApiKey];
      if (!promptShortId) return "";

      const normalizedPromptShortId = this.normalizePromptKey(promptShortId);
      return this.promptLabelByShortId[normalizedPromptShortId] || "";
    },
    formatPromptShortId(value, eoApiKey = null) {
      const adminLinkedPrompt = this.getPromptLabelViaAdminMapping(eoApiKey);
      if (adminLinkedPrompt) return adminLinkedPrompt;

      if (!value) return "-";
      const normalizedValue = this.normalizePromptKey(value);
      return (
        this.promptLabelByApiShortId[normalizedValue] ||
        this.promptLabelByShortId[normalizedValue] ||
        value
      );
    },
    formatStopReason(value) {
      if (!value) return "-";
      return String(value);
    },
    formatUser(value) {
      if (!value) return "-";
      return String(value);
    },
    formatModel(value) {
      if (!value) return "-";
      return String(value);
    },
    formatPromptName(value) {
      if (!value) return "-";
      const normalizedValue = this.normalizePromptKey(value);
      return this.promptLabelByShortId[normalizedValue] || String(value);
    },
    formatResponseFeedbackValue(value) {
      if (value === null || value === undefined || value === "") return null;
      if (value === "neutral") return 0;
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return null;
      if (numeric > 0) return 1;
      if (numeric < 0) return -1;
      return 0;
    },
    formatResponseFeedbackLabel(value) {
      const normalized = this.formatResponseFeedbackValue(value);
      if (normalized === 1) return "Positiv";
      if (normalized === -1) return "Negativ";
      if (normalized === 0) return "Neutral";
      return "-";
    },
    parseLlmResult(value) {
      if (value === null || value === undefined || value === "") return null;

      let parsedValue = value;
      // Handle plain JSON strings and double-stringified JSON.
      for (let i = 0; i < 2; i++) {
        if (typeof parsedValue !== "string") break;
        const trimmed = parsedValue.trim();
        if (!trimmed) return null;
        try {
          parsedValue = JSON.parse(trimmed);
        } catch (err) {
          parsedValue = trimmed;
          break;
        }
      }

      return parsedValue;
    },
    formatLlmResultPreview(value) {
      const parsed = this.parseLlmResult(value);
      if (parsed === null) return "-";

      if (typeof parsed === "object") {
        if (typeof parsed.text === "string" && parsed.text.trim()) {
          return parsed.text;
        }
        try {
          return JSON.stringify(parsed);
        } catch (err) {
          return String(parsed);
        }
      }

      return String(parsed);
    },
    formatLlmResult(value) {
      const parsed = this.parseLlmResult(value);
      if (parsed === null) return "-";
      if (typeof parsed === "object") {
        const blocks = [];
        if (typeof parsed.text === "string" && parsed.text.trim()) {
          blocks.push(`Antwort:\n${parsed.text.trim()}`);
        }
        const model = parsed?.raw?.model || parsed?.model;
        const usage = parsed?.raw?.usage || parsed?.usage;
        if (
          model ||
          usage?.input_tokens !== undefined ||
          usage?.output_tokens !== undefined
        ) {
          const meta = [];
          if (model) meta.push(`Model: ${model}`);
          if (usage?.input_tokens !== undefined)
            meta.push(`Input Tokens: ${usage.input_tokens}`);
          if (usage?.output_tokens !== undefined)
            meta.push(`Output Tokens: ${usage.output_tokens}`);
          blocks.push(meta.join("\n"));
        }
        try {
          blocks.push(`Raw JSON:\n${JSON.stringify(parsed, null, 2)}`);
        } catch (err) {
          blocks.push(`Raw JSON:\n${String(parsed)}`);
        }
        return blocks.join("\n\n");
      }

      return String(parsed);
    },
    buildEoApiKeyLabel(description, apiKey) {
      const safeKey = String(apiKey || "").trim();
      const safeDescription = String(description || "").trim();
      if (!safeKey) return "-";
      if (!safeDescription) return safeKey;
      return safeDescription;
    },
    buildPromptLabel(name, shortId) {
      const safeShortId = String(shortId || "").trim();
      const safeName = String(name || "").trim();
      if (!safeShortId) return "-";
      if (!safeName) return safeShortId;
      return `${safeName} (${safeShortId})`;
    }
  }
};
